import { Inject, Injectable, DOCUMENT } from '@angular/core';

import { Meta, Title } from '@angular/platform-browser';
import {
  BuildOrganizationInput,
  OrganizationSchema,
  SchemaService,
} from './schema.service';

@Injectable({
  providedIn: 'root',
})
export class SeoService {
  constructor(
    @Inject(DOCUMENT) private document: Document,
    private meta: Meta,
    private title: Title,
    private schemaService: SchemaService
  ) {}

  setTitle(value: string): void {
    this.title.setTitle(value);
  }

  setDescription(value: string): void {
    this.meta.updateTag({ name: 'description', content: value });
  }

  setRobots(value: string): void {
    this.meta.updateTag({ name: 'robots', content: value });
  }

  upsertMetaTag(tag: { name?: string; property?: string; content: string }): void {
    if (tag.name) {
      this.meta.updateTag({ name: tag.name, content: tag.content });
      return;
    }
    if (tag.property) {
      this.meta.updateTag({ property: tag.property, content: tag.content });
    }
  }

  setOpenGraph(tags: {
    title?: string;
    description?: string;
    image?: string;
    url?: string;
    type?: string;
  }): void {
    if (tags.title) {
      this.upsertMetaTag({ property: 'og:title', content: tags.title });
    }
    if (tags.description) {
      this.upsertMetaTag({ property: 'og:description', content: tags.description });
    }
    if (tags.image) {
      this.upsertMetaTag({ property: 'og:image', content: tags.image });
    }
    if (tags.url) {
      this.upsertMetaTag({ property: 'og:url', content: tags.url });
    }
    if (tags.type) {
      this.upsertMetaTag({ property: 'og:type', content: tags.type });
    }
  }

  setCanonical(url: string): void {
    const head = this.document.head;
    let link = head.querySelector<HTMLLinkElement>("link[rel='canonical']");
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      head.appendChild(link);
    }
    link.setAttribute('href', url);
  }

  getDefaultOrganizationInput(): BuildOrganizationInput {
    const base = this.schemaService.getStaticOrganizationSchema();
    const cp = Array.isArray(base.contactPoint)
      ? base.contactPoint[0]
      : base.contactPoint;
    return {
      name: base.name,
      url: base.url,
      alternateName: base.alternateName,
      logo: base.logo,
      telephone: cp?.telephone,
      contactType: cp?.contactType,
      contactOption: cp?.contactOption,
      areaServed: cp?.areaServed,
      availableLanguage: cp?.availableLanguage,
      sameAs: base.sameAs,
      type: base['@type'] as 'Corporation' | 'Organization',
      address: base.address
        ? {
            streetAddress: base.address.streetAddress,
            addressLocality: base.address.addressLocality,
            addressRegion: base.address.addressRegion,
            postalCode: base.address.postalCode,
            addressCountry: base.address.addressCountry,
          }
        : undefined,
    };
  }

  applyOrganizationSchemaForCurrentUrl(overrides?: Partial<BuildOrganizationInput>): void {
    const currentUrl = this.document.location?.href ?? '';
    const base = this.getDefaultOrganizationInput();
    const input: BuildOrganizationInput = {
      ...base,
      ...overrides,
      url: overrides?.url ?? currentUrl,
    };
    this.setOrganizationSchema(input);
  }

  private upsertJsonLd(key: string, payload: object): void {
    const head = this.document.head;
    let script = head.querySelector<HTMLScriptElement>(
      `script[type='application/ld+json'][data-schema='${key}']`
    );
    if (!script) {
      const candidates = Array.from(
        head.querySelectorAll<HTMLScriptElement>("script[type='application/ld+json']")
      );
      const found = candidates.find((s) => {
        try {
          const obj = JSON.parse(s.textContent || '{}');
          return obj?.['@type'] === 'Corporation' && key === 'organization';
        } catch {
          return false;
        }
      });
      if (found) script = found;
    }
    if (!script) {
      script = this.document.createElement('script');
      script.type = 'application/ld+json';
      head.appendChild(script);
    }
    script.setAttribute('data-schema', key);
    script.text = this.schemaService.toJsonLd(payload);
  }

  setOrganizationSchema(input: BuildOrganizationInput): void {
    const schema = this.schemaService.buildOrganizationSchema(input);
    const validation = this.schemaService.validateOrganizationSchema(schema);
    if (!validation.valid) return;
    this.upsertJsonLd('organization', schema);
  }

  getOrganizationSchema(): OrganizationSchema | undefined {
    return this.schemaService.getOrganizationSchema();
  }

  removeSchema(key: string): void {
    const head = this.document.head;
    const script = head.querySelector<HTMLScriptElement>(
      `script[type='application/ld+json'][data-schema='${key}']`
    );
    if (script && script.parentNode) {
      script.parentNode.removeChild(script);
    }
  }
}
